import {
  ChevronLeft,
  ClipboardList,
  Link2,
  PackagePlus,
  Search,
  Send,
  ShoppingCart,
  X,
  Check,
  Ban,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";
import { toast } from "sonner";

import styles from "./SolicitacaoCompraPage.module.css";
import { Produto, SolicitacaoCompra } from "../../types/types";

const PROJETO_OPTIONS = [
  "MONITORA",
  "CANAL DE INSPEÇÃO",
  "INSPECIONA",
  "TODOS",
];

const EMPTY_SOLICITACAO = {
  nomeProduto: "",
  especificacoes: "",
  quantidadeSolicitada: 1,
  fornecedor: "",
  projeto: "",
  localizacao: "",
  justificativa: "",
  produtoVinculadoId: null as number | null,
};

const EMPTY_NOVO_PRODUTO = {
  nome: "",
  quantidadeMinima: 0,
  quantidadeMaxima: 0,
  fornecedor: "",
  especificacoes: "",
  projeto: "",
  serveParaRaioX: "",
  localizacao: "",
};

const STATUS_BADGE: Record<string, string> = {
  PENDENTE: styles.statusPouco,
  APROVADO: styles.statusEm,
  REJEITADO: styles.statusSemEstoque,
};

const SolicitacaoCompraPage = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useSessionExpirationCheck(sessionExpiration, token);

  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoCompra[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [statusFiltro, setStatusFiltro] = useState<string>("PENDENTE");

  const [modalNovaShown, setModalNovaShown] = useState(false);
  const [formNova, setFormNova] = useState({ ...EMPTY_SOLICITACAO });
  const [buscaProdutoSemelhante, setBuscaProdutoSemelhante] = useState("");
  const [produtoSelecionadoNova, setProdutoSelecionadoNova] =
    useState<Produto | null>(null);

  const [modalAprovarShown, setModalAprovarShown] = useState(false);
  const [solicitacaoAtual, setSolicitacaoAtual] =
    useState<SolicitacaoCompra | null>(null);
  const [modoAprovacao, setModoAprovacao] = useState<"vincular" | "novo">(
    "vincular",
  );
  const [buscaVinculo, setBuscaVinculo] = useState("");
  const [produtoVinculo, setProdutoVinculo] = useState<Produto | null>(null);
  const [novoProdutoForm, setNovoProdutoForm] = useState({
    ...EMPTY_NOVO_PRODUTO,
  });
  const [observacaoAprovacao, setObservacaoAprovacao] = useState("");

  const loadSolicitacoes = useCallback(
    async (status?: string) => {
      const query = status ? `?status=${status}` : "";
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra${query}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        setSolicitacoes(await res.json());
      }
    },
    [token],
  );

  const loadProdutos = useCallback(async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API}/api/Estoque/GetAllProdutos`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) {
      setProdutos(await res.json());
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/area-do-cliente");
      return;
    }
    document.title = "Íkone | Solicitações de Compra";
    loadSolicitacoes(statusFiltro);
    loadProdutos();
    return () => {};
  }, [dispatch, navigate, sessionExpiration, token, loadProdutos]);

  useEffect(() => {
    loadSolicitacoes(statusFiltro);
  }, [statusFiltro, loadSolicitacoes]);

  // ---------- Nova solicitação ----------

  const sugestoesNova =
    buscaProdutoSemelhante.length > 1
      ? produtos
          .filter((p) =>
            p.nome.toLowerCase().includes(buscaProdutoSemelhante.toLowerCase()),
          )
          .slice(0, 6)
      : [];

  const handleChangeNova = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormNova((prev) => ({ ...prev, [name]: value }));

    if (name === "nomeProduto") {
      setBuscaProdutoSemelhante(value);
      setProdutoSelecionadoNova(null);
      setFormNova((prev) => ({ ...prev, produtoVinculadoId: null }));
    }
  };

  const selecionarProdutoNova = (produto: Produto) => {
    setProdutoSelecionadoNova(produto);
    setFormNova((prev) => ({
      ...prev,
      nomeProduto: produto.nome,
      produtoVinculadoId: produto.id,
    }));
    setBuscaProdutoSemelhante("");
  };

  const submitSolicitacao = async () => {
    if (!formNova.nomeProduto.trim()) {
      toast.error("Informe o nome do item.");
      return;
    }
    if (formNova.quantidadeSolicitada <= 0) {
      toast.error("A quantidade deve ser maior que zero.");
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...formNova,
            solicitanteUsuario: user?.login || "",
          }),
        },
      );

      if (res.ok) {
        toast.success("Solicitação enviada com sucesso.");
        setFormNova({ ...EMPTY_SOLICITACAO });
        setProdutoSelecionadoNova(null);
        setBuscaProdutoSemelhante("");
        setModalNovaShown(false);
        loadSolicitacoes(statusFiltro);
      } else {
        toast.error("Erro ao enviar solicitação.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar solicitação.");
    }
  };

  // ---------- Aprovação ----------

  const abrirAprovacao = (solicitacao: SolicitacaoCompra) => {
    setSolicitacaoAtual(solicitacao);
    setModoAprovacao(solicitacao.produtoVinculadoId ? "vincular" : "novo");
    setObservacaoAprovacao("");
    setBuscaVinculo("");

    const produtoJaVinculado = solicitacao.produtoVinculadoId
      ? produtos.find((p) => p.id === solicitacao.produtoVinculadoId) || null
      : null;
    setProdutoVinculo(produtoJaVinculado);

    setNovoProdutoForm({
      ...EMPTY_NOVO_PRODUTO,
      nome: solicitacao.nomeProduto,
      especificacoes: solicitacao.especificacoes,
      fornecedor: solicitacao.fornecedor,
      projeto: solicitacao.projeto,
      localizacao: solicitacao.localizacao,
    });

    setModalAprovarShown(true);
  };

  const sugestoesVinculo =
    buscaVinculo.length > 1
      ? produtos
          .filter((p) =>
            p.nome.toLowerCase().includes(buscaVinculo.toLowerCase()),
          )
          .slice(0, 6)
      : [];

  const handleChangeNovoProduto = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setNovoProdutoForm((prev) => ({ ...prev, [name]: value }));
  };

  const confirmarAprovacao = async () => {
    if (!solicitacaoAtual) return;

    if (modoAprovacao === "vincular" && !produtoVinculo) {
      toast.error("Selecione o produto existente para vincular.");
      return;
    }
    if (modoAprovacao === "novo" && !novoProdutoForm.nome.trim()) {
      toast.error("Informe o nome do novo produto.");
      return;
    }

    const payload = {
      aprovadorUsuario: user?.login || "",
      observacaoAprovacao: observacaoAprovacao,
      criarNovoProduto: modoAprovacao === "novo",
      produtoVinculadoId:
        modoAprovacao === "vincular" ? produtoVinculo?.id : null,
      novoProduto: modoAprovacao === "novo" ? novoProdutoForm : null,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra/${solicitacaoAtual.id}/aprovar`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (res.ok) {
        toast.success("Solicitação aprovada e estoque atualizado.");
        setModalAprovarShown(false);
        setSolicitacaoAtual(null);
        loadSolicitacoes(statusFiltro);
        loadProdutos();
      } else {
        toast.error("Erro ao aprovar solicitação.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao aprovar solicitação.");
    }
  };

  const rejeitarSolicitacao = async (solicitacao: SolicitacaoCompra) => {
    if (!window.confirm("Deseja rejeitar essa solicitação?")) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/SolicitacaoCompra/${solicitacao.id}/rejeitar`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            aprovadorUsuario: user?.login || "",
            observacao: "",
          }),
        },
      );

      if (res.ok) {
        toast.success("Solicitação rejeitada.");
        loadSolicitacoes(statusFiltro);
      } else {
        toast.error("Erro ao rejeitar solicitação.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao rejeitar solicitação.");
    }
  };

  // ---------- Render ----------

  const renderLinhaSolicitacao = (item: SolicitacaoCompra) => {
    const dataFormatada = new Date(item.dataSolicitacao).toLocaleString(
      "pt-BR",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      },
    );

    return (
      <tr key={item.id}>
        <td
          data-label="Item"
          className={styles.cellName}
          title={item.nomeProduto}
        >
          {item.nomeProduto}
        </td>
        <td data-label="Qtd solicitada">{item.quantidadeSolicitada}</td>
        <td data-label="Fornecedor">{item.fornecedor}</td>
        <td data-label="Solicitante">{item.solicitanteUsuario}</td>
        <td data-label="Data">{dataFormatada}</td>
        <td data-label="Status">
          <span className={`${styles.badge} ${STATUS_BADGE[item.status]}`}>
            <span className={styles.badgeDot} />
            {item.status}
          </span>
        </td>
        <td data-label="Ações">
          {item.status === "PENDENTE" ? (
            <div className={styles.rowActions}>
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
                onClick={() => abrirAprovacao(item)}
                aria-label="Avaliar solicitação"
              >
                <Check size={16} />
              </button>
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
                onClick={() => rejeitarSolicitacao(item)}
                aria-label="Rejeitar solicitação"
              >
                <Ban size={16} />
              </button>
            </div>
          ) : (
            <span className={styles.thumbEmpty}>—</span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate(-1)}
            aria-label="Voltar"
          >
            <ChevronLeft size={18} />
          </button>
          <h1 className={styles.title}>Solicitações de Compra</h1>
        </div>
        <p className={styles.subtitle}>
          Peça a compra de materiais e acompanhe a aprovação, vinculando a um
          item já cadastrado ou criando um novo produto no estoque.
        </p>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.toolbarRow}>
          <div className={styles.spacer} />
          <div className={styles.actionsGroup}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => setModalNovaShown(true)}
            >
              <ShoppingCart size={16} />
              Nova solicitação
            </button>
          </div>
        </div>

        <div className={styles.filters}>
          <select
            className={styles.select}
            value={statusFiltro}
            onChange={(ev) => setStatusFiltro(ev.target.value)}
          >
            <option value="PENDENTE">Pendentes</option>
            <option value="APROVADO">Aprovadas</option>
            <option value="REJEITADO">Rejeitadas</option>
            <option value="">Todas</option>
          </select>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Item</th>
              <th>Qtd solicitada</th>
              <th>Fornecedor</th>
              <th>Solicitante</th>
              <th>Data</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {solicitacoes.length > 0 ? (
              solicitacoes.map(renderLinhaSolicitacao)
            ) : (
              <tr>
                <td colSpan={7}>
                  <div className={styles.emptyState}>
                    <ClipboardList size={32} />
                    <p>Nenhuma solicitação encontrada.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Nova solicitação */}
      <Modal
        show={modalNovaShown}
        onHide={() => setModalNovaShown(false)}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <ShoppingCart size={20} />
              Solicitar compra de material
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.form}>
            <div className={styles.field} style={{ position: "relative" }}>
              <label htmlFor="nomeProduto" className={styles.label}>
                Nome do item
              </label>
              <input
                type="text"
                id="nomeProduto"
                name="nomeProduto"
                className={styles.input}
                value={formNova.nomeProduto}
                onChange={handleChangeNova}
                autoComplete="off"
              />
              {produtoSelecionadoNova && (
                <p className={styles.fileChosen}>
                  <Link2
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: "0.3rem" }}
                  />
                  Vinculado a item já cadastrado: {produtoSelecionadoNova.nome}
                </p>
              )}
              {sugestoesNova.length > 0 && !produtoSelecionadoNova && (
                <div className={styles.historyBox}>
                  {sugestoesNova.map((p) => (
                    <div
                      key={p.id}
                      className={styles.historyItem}
                      style={{ cursor: "pointer" }}
                      onClick={() => selecionarProdutoNova(p)}
                    >
                      <span className={styles.historyIcon}>
                        <Search size={14} />
                      </span>
                      <div className={styles.historyBody}>
                        <span className={styles.historyType}>{p.nome}</span>
                        <span className={styles.historyMeta}>
                          Estoque atual: {p.quantidade} — {p.localizacao}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label htmlFor="quantidadeSolicitada" className={styles.label}>
                  Quantidade
                </label>
                <input
                  type="number"
                  id="quantidadeSolicitada"
                  name="quantidadeSolicitada"
                  className={styles.input}
                  value={formNova.quantidadeSolicitada}
                  onChange={handleChangeNova}
                  min={1}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="fornecedor" className={styles.label}>
                  Fornecedor sugerido
                </label>
                <input
                  type="text"
                  id="fornecedor"
                  name="fornecedor"
                  className={styles.input}
                  value={formNova.fornecedor}
                  onChange={handleChangeNova}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="projeto" className={styles.label}>
                  Projeto
                </label>
                <select
                  id="projeto"
                  name="projeto"
                  className={styles.selectField}
                  value={formNova.projeto}
                  onChange={handleChangeNova}
                >
                  <option value="">Selecione o projeto</option>
                  {PROJETO_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label htmlFor="localizacao" className={styles.label}>
                  Localização
                </label>
                <input
                  type="text"
                  id="localizacao"
                  name="localizacao"
                  className={styles.input}
                  value={formNova.localizacao}
                  onChange={handleChangeNova}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="especificacoes" className={styles.label}>
                Especificações
              </label>
              <textarea
                id="especificacoes"
                name="especificacoes"
                className={styles.textarea}
                value={formNova.especificacoes}
                onChange={handleChangeNova}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="justificativa" className={styles.label}>
                Justificativa
              </label>
              <textarea
                id="justificativa"
                name="justificativa"
                className={styles.textarea}
                value={formNova.justificativa}
                onChange={handleChangeNova}
                placeholder="Por que esse material é necessário?"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setModalNovaShown(false)}
          >
            Fechar
          </button>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={submitSolicitacao}
          >
            <Send size={16} />
            Enviar solicitação
          </button>
        </Modal.Footer>
      </Modal>

      {/* Aprovar solicitação */}
      <Modal
        show={modalAprovarShown}
        onHide={() => setModalAprovarShown(false)}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <Check size={20} />
              Avaliar solicitação — {solicitacaoAtual?.nomeProduto}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {solicitacaoAtual && (
            <div className={styles.form}>
              <p className={styles.subtitle}>
                Solicitado por{" "}
                <strong>{solicitacaoAtual.solicitanteUsuario}</strong> —
                quantidade{" "}
                <strong>{solicitacaoAtual.quantidadeSolicitada}</strong>
                {solicitacaoAtual.justificativa
                  ? ` — "${solicitacaoAtual.justificativa}"`
                  : ""}
              </p>

              <div
                className={styles.actionsGroup}
                style={{ marginBottom: "1rem" }}
              >
                <button
                  type="button"
                  className={`${styles.btn} ${
                    modoAprovacao === "vincular"
                      ? styles.btnGhostActive
                      : styles.btnGhost
                  }`}
                  onClick={() => setModoAprovacao("vincular")}
                >
                  <Link2 size={16} />
                  Vincular a item existente
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${
                    modoAprovacao === "novo"
                      ? styles.btnGhostActive
                      : styles.btnGhost
                  }`}
                  onClick={() => setModoAprovacao("novo")}
                >
                  <PackagePlus size={16} />
                  Cadastrar como novo item
                </button>
              </div>

              {modoAprovacao === "vincular" ? (
                <div className={styles.field} style={{ position: "relative" }}>
                  <label className={styles.label}>Buscar item no estoque</label>
                  <div className={styles.searchWrap}>
                    <Search size={16} />
                    <input
                      type="text"
                      className={styles.searchInput}
                      placeholder="Digite o nome do item..."
                      value={
                        produtoVinculo ? produtoVinculo.nome : buscaVinculo
                      }
                      onChange={(e) => {
                        setBuscaVinculo(e.target.value);
                        setProdutoVinculo(null);
                      }}
                    />
                    {produtoVinculo && (
                      <button
                        type="button"
                        className={styles.searchClearBtn}
                        onClick={() => {
                          setProdutoVinculo(null);
                          setBuscaVinculo("");
                        }}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {sugestoesVinculo.length > 0 && !produtoVinculo && (
                    <div className={styles.historyBox}>
                      {sugestoesVinculo.map((p) => (
                        <div
                          key={p.id}
                          className={styles.historyItem}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setProdutoVinculo(p);
                            setBuscaVinculo("");
                          }}
                        >
                          <div className={styles.historyBody}>
                            <span className={styles.historyType}>{p.nome}</span>
                            <span className={styles.historyMeta}>
                              Estoque atual: {p.quantidade} — {p.localizacao}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {produtoVinculo && (
                    <p className={styles.fileChosen}>
                      Nova quantidade após aprovação:{" "}
                      <strong>
                        {produtoVinculo.quantidade +
                          (solicitacaoAtual.quantidadeSolicitada || 0)}
                      </strong>{" "}
                      (atual {produtoVinculo.quantidade} +{" "}
                      {solicitacaoAtual.quantidadeSolicitada})
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className={styles.field}>
                    <label htmlFor="novoNome" className={styles.label}>
                      Nome do item
                    </label>
                    <input
                      type="text"
                      id="novoNome"
                      name="nome"
                      className={styles.input}
                      value={novoProdutoForm.nome}
                      onChange={handleChangeNovoProduto}
                    />
                  </div>

                  <div className={styles.formGrid}>
                    <div className={styles.field}>
                      <label htmlFor="novoQtdMin" className={styles.label}>
                        Quantidade mínima
                      </label>
                      <input
                        type="number"
                        id="novoQtdMin"
                        name="quantidadeMinima"
                        className={styles.input}
                        value={novoProdutoForm.quantidadeMinima}
                        onChange={handleChangeNovoProduto}
                      />
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="novoQtdMax" className={styles.label}>
                        Quantidade máxima
                      </label>
                      <input
                        type="number"
                        id="novoQtdMax"
                        name="quantidadeMaxima"
                        className={styles.input}
                        value={novoProdutoForm.quantidadeMaxima}
                        onChange={handleChangeNovoProduto}
                      />
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="novoFornecedor" className={styles.label}>
                        Fornecedor
                      </label>
                      <input
                        type="text"
                        id="novoFornecedor"
                        name="fornecedor"
                        className={styles.input}
                        value={novoProdutoForm.fornecedor}
                        onChange={handleChangeNovoProduto}
                      />
                    </div>
                    <div className={styles.field}>
                      <label htmlFor="novoLocalizacao" className={styles.label}>
                        Localização
                      </label>
                      <input
                        type="text"
                        id="novoLocalizacao"
                        name="localizacao"
                        className={styles.input}
                        value={novoProdutoForm.localizacao}
                        onChange={handleChangeNovoProduto}
                      />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label
                      htmlFor="novoEspecificacoes"
                      className={styles.label}
                    >
                      Especificações
                    </label>
                    <textarea
                      id="novoEspecificacoes"
                      name="especificacoes"
                      className={styles.textarea}
                      value={novoProdutoForm.especificacoes}
                      onChange={handleChangeNovoProduto}
                    />
                  </div>

                  <p className={styles.fileChosen}>
                    Será cadastrado com quantidade inicial de{" "}
                    <strong>{solicitacaoAtual.quantidadeSolicitada}</strong>.
                  </p>
                </>
              )}

              <div className={styles.field}>
                <label htmlFor="observacaoAprovacao" className={styles.label}>
                  Observações da aprovação
                </label>
                <textarea
                  id="observacaoAprovacao"
                  className={styles.textarea}
                  value={observacaoAprovacao}
                  onChange={(e) => setObservacaoAprovacao(e.target.value)}
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setModalAprovarShown(false)}
          >
            Fechar
          </button>
          <button
            className={`${styles.btn} ${styles.btnSuccess}`}
            onClick={confirmarAprovacao}
          >
            <Send size={16} />
            Confirmar aprovação
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SolicitacaoCompraPage;
